import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { BurgerOrder, ORDER_STATUS_MN } from '../types';
import { mockDataService } from '../services/mockData';
import './Orders.css';

const Orders: React.FC = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState<BurgerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [useFirebase, setUseFirebase] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const fetchOrders = async () => {
      setLoading(true);
      setError('');
      
      console.log('🔍 Захиалга хайж байна, хэрэглэгч:', currentUser.email);
      
      // Demo config шалгах
      if (auth.app.options.apiKey === 'demo-api-key') {
        console.log('🎮 Demo режимд ажиллаж байна, mock service ашиглана');
        loadMockOrders();
        return;
      }
      
      try {
        if (useFirebase) {
          // Firebase real-time listener ашиглах
          const q = query(
            collection(db, 'orders'),
            where('customerEmail', '==', currentUser.email),
            orderBy('orderDate', 'desc')
          );
          
          console.log('📡 Firebase real-time listener эхлүүлж байна...');
          
          const unsubscribe = onSnapshot(
            q,
            (querySnapshot) => {
              console.log('📊 Firebase snapshot авлаа, docs тоо:', querySnapshot.docs.length);
              
              const ordersData = querySnapshot.docs.map(doc => {
                const data = doc.data();
                console.log('📄 Order data:', { id: doc.id, ...data });
                
                return {
                  id: doc.id,
                  ...data,
                  orderDate: data.orderDate?.toDate ? data.orderDate.toDate() : new Date(data.orderDate)
                };
              }) as BurgerOrder[];
              
              setOrders(ordersData);
              setLoading(false);
              console.log('✅ Firebase захиалгууд ачаалагдлаа:', ordersData.length);
            },
            (firebaseError) => {
              console.error('❌ Firebase snapshot алдаа:', firebaseError);
              console.log('🔄 Demo режимд шилжиж байна...');
              setUseFirebase(false);
              loadMockOrders();
            }
          );
          
          // Cleanup function
          return () => {
            console.log('🧹 Firebase listener цэвэрлэж байна');
            unsubscribe();
          };
        } else {
          loadMockOrders();
        }
      } catch (error: any) {
        console.error('💥 Захиалга татахад алдаа:', error);
        setError('Захиалга ачаалахад алдаа гарлаа');
        setUseFirebase(false);
        loadMockOrders();
      }
    };

    const loadMockOrders = async () => {
      try {
        console.log('🎮 Demo захиалгууд ачаалж байна...');
        mockDataService.initializeDemoData();
        const mockOrders = await mockDataService.getUserOrders(currentUser.email || '');
        
        const processedOrders = mockOrders.map(order => ({
          ...order,
          orderDate: new Date(order.orderDate)
        }));
        
        setOrders(processedOrders);
        setLoading(false);
        console.log('✅ Demo захиалгууд ачаалагдлаа:', processedOrders.length);
      } catch (error) {
        console.error('💥 Demo захиалга ачаалахад алдаа:', error);
        setError('Demo захиалга ачаалахад алдаа гарлаа');
        setLoading(false);
      }
    };

    // localStorage өөрчлөлтийг сонсох listener
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'burger-ai-orders') {
        console.log('🔄 LocalStorage өөрчлөгдлөө, захиалгууд дахин ачаалж байна...');
        loadMockOrders();
      }
    };

    // Storage event listener нэмэх
    window.addEventListener('storage', handleStorageChange);

    const cleanup = fetchOrders();
    
    // Cleanup function буцаах
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (cleanup && typeof cleanup.then === 'function') {
        cleanup.then(unsubscribe => {
          if (typeof unsubscribe === 'function') {
            unsubscribe();
          }
        });
      }
    };
  }, [currentUser, useFirebase]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'status-pending';
      case 'confirmed':
      case 'preparing':
        return 'status-confirmed';
      case 'ready':
      case 'delivered':
        return 'status-delivered';
      default:
        return '';
    }
  };

  if (!currentUser) {
    return (
      <div className="orders-page">
        <div className="auth-required">
          <h2>Нэвтрэх шаардлагатай</h2>
          <p>Захиалгуудыг харахын тулд нэвтэрнэ үү.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="orders-page">
        <div className="loading">
          <div className="spinner"></div>
          <p>Захиалгууд ачааллаж байна...</p>
          <small>{useFirebase ? 'Firebase-аас' : 'Demo режимээс'} ачаалж байна...</small>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="orders-page">
        <div className="error-message">
          <h3>❌ Алдаа гарлаа</h3>
          <p>{error}</p>
          <button 
            onClick={() => {
              setError('');
              setUseFirebase(!useFirebase);
            }}
            className="btn btn-secondary"
          >
            {useFirebase ? 'Demo режим ашиглах' : 'Firebase дахин оролдох'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="orders-page">
      <div className="orders-header">
        <h1>Миний захиалгууд</h1>
        <p>Таны хийсэн захиалгуудын жагсаалт</p>
        <button 
          onClick={() => window.location.reload()} 
          className="btn btn-secondary refresh-btn"
          title="Захиалгын статусыг шинэчлэх"
        >
          🔄 Шинэчлэх
        </button>
      </div>

      {orders.length === 0 ? (
        <div className="no-orders">
          <div className="no-orders-icon">🍔</div>
          <h3>Захиалга байхгүй байна</h3>
          <p>Та одоог хүртэл ямар нэгэн захиалга хийгээгүй байна.</p>
          <a href="/burger-builder" className="btn btn-primary">
            Анхны захиалгаа хийх
          </a>
        </div>
      ) : (
        <div className="orders-list">
          {orders.map((order) => (
            <div key={order.id} className="order-card">
              <div className="order-header">
                <div className="order-info">
                  <h3>Захиалга #{order.id?.slice(-6)}</h3>
                  <p className="order-date">
                    {order.orderDate.toLocaleDateString('mn-MN', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div className={`order-status ${getStatusColor(order.status)}`}>
                  {ORDER_STATUS_MN[order.status]}
                </div>
              </div>

              <div className="order-details">
                <div className="order-ingredients">
                  <h4>Бургерийн орцууд:</h4>
                  <ul>
                    {order.ingredients.map((ingredient, index) => (
                      <li key={index}>
                        {ingredient.nameMn} - {ingredient.price.toLocaleString()}₮
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="order-customer">
                  <h4>Хүргэлтийн мэдээлэл:</h4>
                  <p><strong>Нэр:</strong> {order.customerName}</p>
                  <p><strong>Утас:</strong> {order.customerPhone}</p>
                  <p><strong>Хаяг:</strong> {order.deliveryAddress}</p>
                </div>
              </div>

              <div className="order-total">
                <strong>Нийт дүн: {order.totalPrice.toLocaleString()}₮</strong>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;
